import {
  createGlobalState, debounceFilter, EventFilter, useAsyncState, useEventBus
} from '@vueuse/core';
import { useRoute } from 'vue2-helpers/vue-router';
import { computed, ref, watch } from 'vue';
import {
  ComponentAddressData,
  ComponentInvoiceBasisData,
  ComponentInvoicePaymentData,
  ComponentInvoice,
  ComponentInvoiceTextData,
  Invoice,
  InvoiceAttachment,
  InvoiceItem,
  InvoiceStatus,
  InvoiceUserType, PayloadAttachment, PayloadCancel,
  PayloadCreateInvoice,
  PayloadExtendInvoice,
  PayloadUpdateInvoice, InvoiceDueStatus, SalutationType, StorableComponents, TypeOfPopUp, PayloadUpdateReadDateInvoice
} from '@/models/Invoice';
import {
  addAttachment,
  cancelInvoice, checkInvoice,
  createInvoice,
  deleteAttachment,
  deleteInvoice,
  extendInvoice, getAttachment,
  getInvoice,
  getInvoices,
  paidInvoice,
  sendInvoice,
  updateInvoice,
  updatePosition, updateReadDateInvoice, viewInvoice
} from '@/services/InvoiceService';
import { getAllContracts } from '@/services/ContractService';
import { ContractStatus } from '@/models/Contracts';
import useUser from '@/mixins/useUser';
import { getCountryCities, getCountryCitiesByPostCode } from '@/services/Geo/Api';
import TranslatedBackendErrors from '@/utils/TranslatedBackendErrors';
import { BcxFormFieldErrorMessage } from '@/models/BcxForm';
import useBCXConfirm from '@/mixins/useBCXConfirm';

const useInvoice = createGlobalState(() => {
  const route = useRoute();
  const invoiceId = computed(() => route.params?.invoiceId ?? '');
  const { userId } = useUser('self');
  const { showConfirm: showConfirmInvoice, ask: askInvoice, onConfirm: onConfirmInvoice } = useBCXConfirm();
  const events = useEventBus<TypeOfPopUp>('invoices');
  const errorMessages = ref<Record<string, BcxFormFieldErrorMessage>>({});
  const errorMessagesAddressDataSender = ref<Record<string, BcxFormFieldErrorMessage>>({});
  const errorMessagesAddressDataReceiver = ref<Record<string, BcxFormFieldErrorMessage>>({});
  const errorMessagesPositions = ref<Array<Record<string, BcxFormFieldErrorMessage>>>([]);
  const unsavedAreas = ref<StorableComponents>({
    addressData: { isUnsaved: false, isProcessing: null },
    paymentData: { isUnsaved: false, isProcessing: null },
    positionsData: { isUnsaved: false, isProcessing: null }
  });
  const isSomethingUnsaved = computed(() => unsavedAreas.value.addressData.isUnsaved
    || unsavedAreas.value.paymentData.isUnsaved
    || unsavedAreas.value.positionsData.isUnsaved);
  const saveUnsavedChanges = ref<'save' | 'doNotSave' | 'cancel'>('doNotSave');

  // ------------------- Generally used  -------------------------------
  /**
   * Determines the revised status of an invoice.
   * - Returns the current status if not unpaid.
   * - Maps dueStatus to "Due".
   * - Otherwise, checks if the current user is the sender to decide between "Sent" or "Open".
   *
   * @param {Invoice} obj - The invoice object.
   * @returns {InvoiceStatus} The revised invoice status.
   */
  function revisedStatus(obj: Invoice): InvoiceStatus {
    if (obj.status !== InvoiceStatus.Unpaid) return obj.status;

    if (obj.dueStatus === InvoiceDueStatus.Due || obj.dueStatus === InvoiceDueStatus.Overdue) return InvoiceStatus.Due;

    const isSender = obj?.sender.user?.userId === userId.value;
    return isSender ? InvoiceStatus.Sent : InvoiceStatus.Open;
  }

  //
  // ------------------- Creation of payloadsError handling of API requests -------------------------------
  //

  function errorHandlingApiRequests(error: any) {
    const translatedError = new TranslatedBackendErrors(error.response);
    translatedError.outputGlobalErrorMessage();
  }

  function postProcessingInvoice(data: Invoice) {
    if (!data) return data;
    const salutationMap: Record<string, SalutationType> = {
      NONE: 'NONE',
      'Mr.': 'MR',
      Herr: 'MR',
      'Ms.': 'MS',
      Frau: 'MS'
    };

    if (data.sender.user.salutation) data.sender.user.salutation = salutationMap[data.sender.user.salutation];
    if (data.receiver.user.salutation) data.receiver.user.salutation = salutationMap[data.receiver.user.salutation];
    data.status = revisedStatus(data);
    return data;
  }

  //
  // ------------------- All API requests  -------------------------------
  //

  const asyncOptions = {
    immediate: false, delay: 0, onError: (err: unknown) => errorHandlingApiRequests(err), throwError: true
  };
  const { state: invoices, execute: fetchInvoices } = useAsyncState(
    () => getInvoices().then((arr) => arr.map(postProcessingInvoice)),
    [],
    { ...asyncOptions, resetOnExecute: false }
  );
  const {
    state: invoice,
    execute: fetchInvoice
  } = useAsyncState<Invoice | null, [], false>(() => getInvoice(invoiceId.value).then(postProcessingInvoice), null, {
    ...asyncOptions,
    shallow: false,
    resetOnExecute: false
  });
  const { execute: fetchUpdateInvoice } = useAsyncState((payload: PayloadUpdateInvoice) => updateInvoice(invoiceId.value, payload), null, asyncOptions);
  const { execute: fetchCreateInvoice } = useAsyncState((payload: PayloadCreateInvoice) => createInvoice(payload), null, asyncOptions);
  const { execute: fetchDeleteInvoice } = useAsyncState(() => deleteInvoice(invoiceId.value), null as Awaited<ReturnType<typeof deleteInvoice>> | null, asyncOptions);
  const { execute: fetchExtendInvoice } = useAsyncState((payload: PayloadExtendInvoice, invoiceNumber = invoiceId.value) => extendInvoice(invoiceNumber, payload), null, asyncOptions);
  const { execute: fetchPaidInvoice } = useAsyncState((invoiceNumber: string) => paidInvoice(invoiceNumber), null as Awaited<ReturnType<typeof paidInvoice>> | null, asyncOptions);
  const {
    execute: fetchSendInvoice,
    isLoading: isSendInvoiceLoading
  } = useAsyncState(() => sendInvoice(invoiceId.value), null as Awaited<ReturnType<typeof sendInvoice>> | null, asyncOptions);
  const { execute: fetchViewInvoice, isLoading: isViewInvoiceLoading } = useAsyncState(() => viewInvoice(invoiceId.value), null as Awaited<ReturnType<typeof viewInvoice>> | null, asyncOptions);
  const { execute: fetchCancelInvoice } = useAsyncState((payload: PayloadCancel, invoiceNumber = invoiceId.value) => cancelInvoice(invoiceNumber, payload), null as Awaited<ReturnType<typeof cancelInvoice>> | null, asyncOptions);
  const { execute: fetchUpdateReadDateInvoice } = useAsyncState((idOfInvoice: string, payload: PayloadUpdateReadDateInvoice) => updateReadDateInvoice(idOfInvoice, payload), null as Awaited<ReturnType<typeof updateReadDateInvoice>> | null, asyncOptions);
  const { execute: fetchCheckInvoice } = useAsyncState(() => checkInvoice(invoiceId.value), null as Awaited<ReturnType<typeof checkInvoice>> | null, asyncOptions);

  // ------------------- All Positions requests  -------------------------------
  const { execute: fetchUpdatePosition } = useAsyncState((payload: InvoiceItem[]) => updatePosition(invoiceId.value, payload), null as Awaited<ReturnType<typeof sendInvoice>> | null, asyncOptions);

  // ------------------- All Attachments requests  -------------------------------
  const { execute: fetchAttachment } = useAsyncState((attachmentId: string) => getAttachment(attachmentId), null as Awaited<ReturnType<typeof sendInvoice>> | null, asyncOptions);
  const {
    execute: fetchAddAttachment,
    isLoading: isAddAttachmentLoading
  } = useAsyncState((payload: PayloadAttachment) => addAttachment(invoiceId.value, payload), null as Awaited<ReturnType<typeof sendInvoice>> | null, asyncOptions);
  const {
    execute: fetchDeleteAttachment,
    isLoading: isDeleteAttachmentLoading
  } = useAsyncState((attachmentId: string) => deleteAttachment(invoiceId.value, attachmentId), null as Awaited<ReturnType<typeof sendInvoice>> | null, asyncOptions);

  // ------------------- Contract Data  -------------------------------
  const { state: contractsData, execute: fetchAllContracts } = useAsyncState(() => getAllContracts(), [] as Awaited<ReturnType<typeof getAllContracts>>, asyncOptions);

  //
  // ------------------- All new objects for the components, with the possibility to change changed data back to the source  -------------------------------
  //

  function calculateTotalAmount(items: InvoiceItem[], taxRate: number): number {
    const totalNet = items.reduce((acc, cur) => acc + (+cur.amount * +cur.costSingle), 0);
    const addedVAT = totalNet * (taxRate / 100);
    return totalNet + addedVAT;
  }

  function mapInvoice(invoice: Invoice, user: any): ComponentInvoice {
    return {
      invoiceId: invoice.invoiceId || '',
      user: user || {},
      reference: invoice.reference || '',
      costCenter: invoice.costCenter || '',
      dueDate: invoice.dueDate || '',
      status: invoice.status || InvoiceStatus.Draft,
      bcReferenceId: invoice.bcReferenceId || '',
      issueDate: invoice.issueDate || '',
      invoiceDocId: invoice.invoiceDocId || '',
      totalAmount: calculateTotalAmount(invoice.items || [], invoice.account.taxRate || 0),
      contractBytecookieId: invoice.contractReference.contractBytecookieId || '',
      contractId: invoice.contractReference.contractId || '',
      created: invoice.created || '',
      currency: invoice.account.currency || 'EUR',
      dueStatus: invoice.dueStatus || 'NONE',
      readDate: invoice.readDate || '',
      lastUpdated: invoice.lastUpdated || '',
      isUserSender: invoice.sender?.user?.userId === userId.value,
      invoicePaidMarkedBy: invoice.invoicePaidMarkedBy || '',
      changedDate: invoice.changedDate || '',
      sentDate: invoice.sentDate || '',
      expansionDays: invoice.expansionDays,
      invoiceCancelDocId: invoice.invoiceCancelDocId
    };
  }

  const componentInvoicesUserAsSender = computed<ComponentInvoice[]>(() => (invoices.value ?? []).map((invoice: Invoice) => mapInvoice(invoice, invoice.sender.user)));

  const componentInvoicesUserAsReceiver = computed<ComponentInvoice[]>(() => (invoices.value ?? []).map((invoice: Invoice) => mapInvoice(invoice, invoice.receiver.user)));

  function getAddressData(userType: InvoiceUserType): ComponentAddressData {
    const invoiceObj = invoice.value?.[userType];
    const postalCode = invoiceObj?.address?.postalCode || '';
    const city = invoiceObj?.address?.city || '';
    return <ComponentAddressData>{
      street: invoiceObj?.address?.street || '',
      houseNumber: invoiceObj?.address?.houseNumber || '',
      supplement: invoiceObj?.address?.supplement || '',
      countryIsoCode: invoiceObj?.address?.countryIsoCode || '',
      postalCode,
      city,
      company: invoiceObj?.user.company?.name || '',
      salutation: invoiceObj?.user?.salutation || 'NONE',
      firstname: invoiceObj?.user?.firstname || '',
      lastname: invoiceObj?.user?.lastname || '',
      phoneNumber: invoiceObj?.address?.phoneNumber || '',
      registerNumber: invoiceObj?.registerNumber || '',
      registerEntity: invoiceObj?.registerEntity || '',
      zipAndCity: [postalCode, city].filter(Boolean).join(', '),
    };
  }

  function setAddressData(addressData: ComponentAddressData, userType: InvoiceUserType) {
    if (!invoice.value) return;
    const invoiceObj = invoice.value[userType];
    if (!invoiceObj) return;

    // If one of the properties does not exist, they are created
    invoiceObj.address ??= {
      city: '', countryIsoCode: '', houseNumber: '', phoneNumber: '', postalCode: '', street: '', supplement: ''
    };
    invoiceObj.user ??= {};
    invoiceObj.user.company ??= { id: '', name: '', rootUserId: '' };

    invoiceObj.address.street = addressData.street;
    invoiceObj.address.houseNumber = addressData.houseNumber;
    invoiceObj.address.supplement = addressData.supplement;
    invoiceObj.address.countryIsoCode = addressData.countryIsoCode;
    invoiceObj.address.postalCode = addressData.postalCode;
    invoiceObj.address.city = addressData.city;
    invoiceObj.user.company.name = addressData.company;
    invoiceObj.user.salutation = addressData.salutation;
    invoiceObj.user.firstname = addressData.firstname;
    invoiceObj.user.lastname = addressData.lastname;
    invoiceObj.address.phoneNumber = addressData.phoneNumber;
    invoiceObj.registerNumber = addressData.registerNumber;
    invoiceObj.registerEntity = addressData.registerEntity;
  }

  const componentAddressDataSender = computed<ComponentAddressData>({
    get() {
      return getAddressData('sender');
    },
    set(addressData) {
      setAddressData(addressData, 'sender');
    }
  });

  const componentAddressDataReceiver = computed<ComponentAddressData>({
    get() {
      return getAddressData('receiver');
    },
    set(addressData) {
      setAddressData(addressData, 'receiver');
    }
  });

  const componentPaymentData = computed<ComponentInvoicePaymentData>({
    get() {
      const invoiceObj = invoice.value?.account;

      return {
        accountName: invoiceObj?.accountName || '',
        iban: invoiceObj?.iban || '',
        bic: invoiceObj?.bic || '',
        currency: invoiceObj?.currency || 'EUR',
        taxId: invoiceObj?.taxId || '',
        taxRate: invoiceObj?.taxRate ?? 19,
        reverseCharge: invoiceObj?.reverseCharge || false,
        reverseChargeTaxId: invoiceObj?.reverseChargeTaxId || ''
      };
    },
    set(newVal) {
      if (!invoice.value) return;

      // If one of the properties does not exist, they are created
      invoice.value.account ??= {
        accountName: '',
        bic: '',
        currency: 'EUR',
        iban: '',
        id: '',
        reverseCharge: false,
        reverseChargeTaxId: '',
        taxId: '',
        taxRate: 0
      };
      const invoiceAccount = invoice.value.account;

      invoiceAccount.accountName = newVal.accountName;
      invoiceAccount.iban = newVal.iban;
      invoiceAccount.bic = newVal.bic;
      invoiceAccount.currency = newVal.currency;
      invoiceAccount.taxId = newVal.taxId;
      invoiceAccount.taxRate = newVal.taxRate;
      invoiceAccount.reverseCharge = newVal.reverseCharge;
      invoiceAccount.reverseChargeTaxId = newVal.reverseChargeTaxId;
    }
  });

  const componentInvoiceBasisData = computed<ComponentInvoiceBasisData>({
    get(): ComponentInvoiceBasisData {
      const invoiceObj = invoice.value ?? {} as Invoice;

      return {
        invoiceDocId: invoiceObj?.invoiceDocId || '',
        invoiceType: invoiceObj.invoiceType ?? 'INVOICE',
        issueDate: invoiceObj.issueDate ?? '',
        performancePeriodFrom: invoiceObj.performancePeriodFrom ?? '',
        performancePeriodTo: invoiceObj.performancePeriodTo ?? '',
        dueDate: invoiceObj.dueDate ?? '',
        associatedContract: invoiceObj.contractReference?.contractBytecookieId ?? '',
        reference: invoiceObj.reference ?? '',
        invoiceCorrectionDocId: invoiceObj.invoiceCorrectionDocId ?? '',
      };
    },
    set(newVal: ComponentInvoiceBasisData) {
      if (!invoice.value) return;

      // If one of the properties does not exist, they are created
      invoice.value.contractReference ??= {
        contractBytecookieId: '', contractId: ''
      };

      invoice.value.invoiceDocId = newVal.invoiceDocId;
      invoice.value.invoiceType = newVal.invoiceType;
      invoice.value.issueDate = newVal.issueDate;
      invoice.value.performancePeriodFrom = newVal.performancePeriodFrom;
      invoice.value.performancePeriodTo = newVal.performancePeriodTo;
      invoice.value.dueDate = newVal.dueDate;
      invoice.value.contractReference.contractBytecookieId = newVal.associatedContract;
      invoice.value.reference = newVal.reference;
    }
  });

  const componentInvoiceTextData = computed<ComponentInvoiceTextData>({
    get() {
      const invoiceObj = invoice.value;

      return {
        intro: invoiceObj?.intro || '',
        footer: invoiceObj?.footer || ''
      };
    },
    set(newVal) {
      if (!invoice.value) return;

      invoice.value.intro = newVal.intro;
      invoice.value.footer = newVal.footer;
    }
  });

  const componentInvoicePositionsData = computed<InvoiceItem[]>({
    get(): InvoiceItem[] {
      return invoice.value?.items ?? [];
    },
    set(newVal: InvoiceItem[]) {
      if (!invoice.value || !invoice.value.items) return;
      invoice.value.items = newVal;
    }
  });

  const componentAttachmentsData = computed<InvoiceAttachment[]>({
    get(): InvoiceAttachment[] {
      return (invoice.value?.attachments ?? []);
    },
    set(newVal: InvoiceAttachment[]) {
      if (!invoice.value || !invoice.value.attachments) return;
      invoice.value.attachments = newVal;
    }
  });

  // ------------------- Generally used  -------------------------------

  const canInvoiceBeEdited = computed(() => invoice.value?.status === InvoiceStatus.Draft && !isSendInvoiceLoading.value);
  const isAttachmentRequestRunning = computed(() => isAddAttachmentLoading.value || isDeleteAttachmentLoading.value);
  const debounceFilterStatus = ref({
    basisdata: false,
    texts: false
  });

  /**
   * Fetches a list of cities for a specific country, based on a search string that can be either a postal code or a city name.
   * If the search string is less than 3 characters, an empty array is returned.
   * Depending on whether the search string consists only of digits, it searches by postal code or by city name.
   *
   * @param countryId - The country identifier (ISO code) for the search.
   * @param searchString - The search string (postal code or city name).
   * @returns A Promise resolving to an array of found cities.
   */
  async function getCitiesByPostCodeOrCity(countryId: string, searchString: string) {
    if (searchString.length < 3) {
      return [];
    }

    const isPostalCode = /^\d+$/.test(searchString);
    const fetchFn = isPostalCode ? getCountryCitiesByPostCode : getCountryCities;

    return fetchFn(countryId, searchString);
  }

  /**
   * Computes a filtered list of contracts that are not canceled
   * and where the current user is not the client.
   *
   * @returns A ComputedRef containing the filtered contracts.
   */
  const filteredContracts = computed(() => contractsData.value.filter((contract) => {
    const notCanceled = contract.status !== ContractStatus.Canceled;
    const userIsNotClient = userId.value !== (contract.client.user?.userId || contract.client.company?.rootUserId);

    return notCanceled && userIsNotClient;
  }));

  /**
   * Finds the keys of properties that have changed between two objects.
   *
   * @template T - The object type.
   * @param {T} newValue - The new object.
   * @param {T} oldValue - The old object.
   * @returns {Array<keyof T>} An array of keys whose values differ.
   */
  function findChangedProps<T extends Record<string, any>>(newValue: T, oldValue: T): Array<keyof T> {
    const keys = Object.keys(newValue || {}) as Array<keyof T>;
    return keys.filter((k) => newValue[k] !== oldValue[k]);
  }

  function resetUnsavedAreasIsProcessing() {
    unsavedAreas.value.addressData.isProcessing = null;
    unsavedAreas.value.paymentData.isProcessing = null;
    unsavedAreas.value.positionsData.isProcessing = null;
  }

  /**
   * Creates a debounced event filter that triggers a callback at start.
   */
  const createDebounceWithCallback = (ms: number, onStart: () => void): EventFilter => {
    const filter = debounceFilter(ms);
    return (invoke, options) => {
      onStart();
      filter(invoke, options);
    };
  };

  /**
   * Waits until all debounce filters have finished or times out after 3 seconds.
   */
  function waitForDebounceEnd() {
    if (!debounceFilterStatus.value.basisdata && !debounceFilterStatus.value.texts) return Promise.resolve();
    let timer: ReturnType<typeof setTimeout>;

    return new Promise<void>((resolve) => {
      const stop = watch(debounceFilterStatus, (v) => {
        if (!v.basisdata && !v.texts) {
          clearTimeout(timer);
          stop();
          resolve();
        }
      }, { deep: true });

      timer = setTimeout(() => {
        stop();
        resolve();
      }, 3000);
    });
  }

  return {
    showConfirmInvoice,
    askInvoice,
    onConfirmInvoice,
    events,
    errorMessages,
    errorMessagesPositions,
    errorMessagesAddressDataSender,
    errorMessagesAddressDataReceiver,
    unsavedAreas,
    isSomethingUnsaved,
    saveUnsavedChanges,
    invoices,
    fetchInvoices,
    invoiceId,
    invoice,
    fetchInvoice,
    componentInvoicesUserAsSender,
    componentInvoicesUserAsReceiver,
    componentAddressDataSender,
    componentAddressDataReceiver,
    componentPaymentData,
    componentInvoiceBasisData,
    componentInvoiceTextData,
    componentInvoicePositionsData,
    componentAttachmentsData,
    fetchUpdateInvoice,
    fetchCreateInvoice,
    fetchDeleteInvoice,
    fetchExtendInvoice,
    fetchPaidInvoice,
    fetchSendInvoice,
    isSendInvoiceLoading,
    fetchViewInvoice,
    isViewInvoiceLoading,
    fetchUpdatePosition,
    fetchCancelInvoice,
    fetchUpdateReadDateInvoice,
    fetchCheckInvoice,
    fetchAttachment,
    fetchAddAttachment,
    fetchDeleteAttachment,
    filteredContracts,
    fetchAllContracts,
    getCitiesByPostCodeOrCity,
    canInvoiceBeEdited,
    isAttachmentRequestRunning,
    findChangedProps,
    resetUnsavedAreasIsProcessing,
    createDebounceWithCallback,
    debounceFilterStatus,
    waitForDebounceEnd,
  };
});

export default useInvoice;
